import type React from "react";

interface PlayerWidgetProps {
  url?: string | null;
}

export const PlayerWidget: React.FC<PlayerWidgetProps> = ({ url }) => {
  if (!url) return null;

  const trimmedUrl = url.trim();
  if (!trimmedUrl) return null;

  let parsedUrl: URL | null = null;
  try {
    parsedUrl = new URL(trimmedUrl);
  } catch {
    try {
      parsedUrl = new URL(`https://${trimmedUrl}`);
    } catch {
      return <FallbackLink url={trimmedUrl} />;
    }
  }

  const hostname = parsedUrl.hostname.toLowerCase();

  if (hostname.includes("soundcloud.com")) {
    return <SoundCloudPlayer url={trimmedUrl} />;
  }

  if (hostname.includes("spotify.com")) {
    return <SpotifyPlayer url={trimmedUrl} parsedUrl={parsedUrl} />;
  }

  if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
    return <YouTubePlayer url={trimmedUrl} parsedUrl={parsedUrl} />;
  }

  return <FallbackLink url={trimmedUrl} />;
};

const SoundCloudPlayer: React.FC<{ url: string }> = ({ url }) => {
  let src = url;
  if (!url.includes("w.soundcloud.com/player")) {
    src = `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%235ecfbe&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`;
  }

  const height = src.includes("visual=true") ? 300 : 166;

  return (
    <div className="w-full rounded-2xl overflow-hidden border border-white/5 bg-black/20 shadow-md">
      <iframe
        width="100%"
        height={height}
        scrolling="no"
        frameBorder="no"
        allow="autoplay"
        src={src}
        title="SoundCloud Player"
        loading="lazy"
      />
    </div>
  );
};

const SpotifyPlayer: React.FC<{ url: string; parsedUrl: URL }> = ({ url, parsedUrl }) => {
  let src = url;
  if (!url.includes("open.spotify.com/embed/")) {
    const pathname = parsedUrl.pathname;
    src = `https://open.spotify.com/embed${pathname}${parsedUrl.search}`;
  }

  const isTrack = parsedUrl.pathname.includes("/track/");
  const height = isTrack ? 80 : 352;

  return (
    <div className="w-full rounded-2xl overflow-hidden border border-white/5 bg-black/20 shadow-md">
      <iframe
        src={src}
        width="100%"
        height={height}
        frameBorder="0"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        title="Spotify Player"
        style={{ borderRadius: "12px" }}
      />
    </div>
  );
};

const YouTubePlayer: React.FC<{ url: string; parsedUrl: URL }> = ({ url, parsedUrl }) => {
  let embedUrl = "";
  const listParam = parsedUrl.searchParams.get("list");
  const hostname = parsedUrl.hostname.toLowerCase();

  if (listParam) {
    embedUrl = `https://www.youtube.com/embed/videoseries?list=${listParam}`;
  } else if (hostname.includes("youtu.be")) {
    const videoId = parsedUrl.pathname.substring(1);
    if (videoId) {
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    }
  } else if (hostname.includes("youtube.com")) {
    if (parsedUrl.pathname.includes("/embed/")) {
      embedUrl = url;
    } else {
      const videoId = parsedUrl.searchParams.get("v");
      if (videoId) {
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
    }
  }

  if (!embedUrl) {
    return <FallbackLink url={url} />;
  }

  return (
    <div className="w-full relative aspect-video rounded-2xl overflow-hidden border border-white/5 bg-black/20 shadow-md">
      <iframe
        className="absolute inset-0 w-full h-full"
        src={embedUrl}
        title="YouTube player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
};

const FallbackLink: React.FC<{ url: string }> = ({ url }) => {
  let label = "Weboldal / Lejátszó";
  try {
    const domain = new URL(url).hostname.replace("www.", "");
    if (domain.includes("mixcloud")) label = "Mixcloud";
    else if (domain.includes("bandcamp")) label = "Bandcamp";
    else if (domain.includes("soundcloud")) label = "SoundCloud";
    else if (domain.includes("spotify")) label = "Spotify";
    else if (domain.includes("youtube") || domain.includes("youtu.be")) label = "YouTube";
    else label = domain;
  } catch {}

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full p-4 rounded-2xl border border-white/5 bg-[#222222]/40 hover:bg-[#333333]/50 hover:border-main/40 transition-all group"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-main/10 border border-main/20 flex items-center justify-center text-main group-hover:bg-main/20 transition-all">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              role="img"
              aria-label="Link icon"
            >
              <title>Link</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-white group-hover:text-main transition-colors">
              Megnyitás a(z) {label} oldalon
            </p>
            <p className="text-xs text-muted truncate max-w-[240px] md:max-w-md">{url}</p>
          </div>
        </div>
        <span className="text-muted group-hover:text-white transition-colors">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <title>Nyíl</title>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </a>
  );
};
